import React, { useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import Spinner from "react-bootstrap/Spinner";

import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import DataProtectionHistoryLogService from "src/modules/data-protection/apis/DataProtectionHistoryLogService";
import CommonService from "src/modules/data-protection/apis/CommonService";

import { toast } from "react-toastify";

import {
    HistoryLogActions,
    HistoryLogSections,
    HistoryLogRoles,
    ActionsMetaData,
} from "src/modules/data-protection/constants";

import {
    INTERNATIONAL_DATE_TIME_FORMAT
} from "src/common/constants";

import DateUtils from "src/services/utils/DateUtils";

const DetailsModal = ({
    t,
    logId,
    onClose
}) => {
    const history = useHistory();
    const [showModal, setShowModal] = useState(true);

    const dateUtils = new DateUtils();

    const {
        isFetching: logDataIsFetching,
        isLoading: logDataIsLoading,
        data: fetchedLogData,
    } = useQuery({
        queryKey: ["data-protection-history-log-details", logId],
        queryFn: async () => {
            const service = DataProtectionHistoryLogService.getInstance();

            return await service.fetchLogDetails(logId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching history log details."), {
                type: "error",
            });
        },
    });

    const { data: users } = useQuery({
        queryKey: ["users"],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.analystAdminsAndAnalysts();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            toast(t("An error occured while fetching users."), {
                type: "error",
            });
        },
    });

    const logData = useMemo(() => {
        if (!fetchedLogData) {
            return null;
        }

        if (fetchedLogData.user?.full_name || fetchedLogData.full_name || fetchedLogData.user_name) {
            return fetchedLogData;
        }

        if (fetchedLogData.user_id && users?.length) {
            const selectedUser = users.find((user) => user.id === fetchedLogData.user_id);

            if (selectedUser) {
                return {
                    ...fetchedLogData,
                    full_name: `${selectedUser.first_name} ${selectedUser.last_name}`,
                };
            }
        }

        return fetchedLogData;
    }, [fetchedLogData, users]);

    const toggle = () => {
        const runCloseEvent = showModal;

        setShowModal(!showModal);

        runCloseEvent && onClose && onClose();
    };

    const itemMetaData = ActionsMetaData[logData?.section] && ActionsMetaData[logData?.section][logData?.action]
        ? ActionsMetaData[logData?.section][logData?.action]
        : [];

    const renderDetailsBody = () => {
        return (
            <div className="p-5 pt-1 pb-1">
                <Row className="mb-3">
                    <Col sm="6" md="4">
                        <span className="detail-label">
                            {t("Activites")}
                        </span>
                    </Col>
                    <Col sm="6" md="8">
                        <span className="detail-value bold">
                            {t(HistoryLogSections[logData?.section])}
                        </span>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col sm="6" md="4">
                        <span className="detail-label">
                            {t("Event ID")}
                        </span>
                    </Col>
                    <Col sm="6" md="8">
                        <span className="detail-value">
                            {logData?.secondary_id}
                        </span>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col sm="6" md="4">
                        <span className="detail-label">
                            {t("User")}
                        </span>
                    </Col>
                    <Col sm="6" md="8">
                        <span className="detail-value">
                            {logData?.user?.full_name || logData?.full_name || logData?.user_name}
                        </span>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col sm="6" md="4">
                        <span className="detail-label">
                            {t("User Role")}
                        </span>
                    </Col>
                    <Col sm="6" md="8">
                        <span className="detail-value">
                            {t(HistoryLogRoles[logData?.user_role])}
                        </span>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col sm="6" md="4">
                        <span className="detail-label">
                            {t("Date")}
                        </span>
                    </Col>
                    <Col sm="6" md="8">
                        <span className="detail-value light">
                            <i className="ri-time-line"></i>&nbsp;
                            {dateUtils.convertDateToDate(logData?.created_at, INTERNATIONAL_DATE_TIME_FORMAT)}
                        </span>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col sm="6" md="4">
                        <span className="detail-label">
                            {t("Browser/Location")}
                        </span>
                    </Col>
                    <Col sm="6" md="8">
                        <span className="detail-value">
                            {logData?.platform && logData?.browser
                                ? `${logData.platform} - ${logData.browser}`
                                : "-"}
                        </span>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col sm="6" md="4">
                        <span className="detail-label">
                            {t("Description")}
                        </span>
                    </Col>
                    <Col sm="6" md="8">
                        <span className="detail-value">
                            {
                                HistoryLogActions[logData?.section] && HistoryLogActions[logData?.section][logData?.action] && (
                                    <>{t(HistoryLogActions[logData?.section][logData?.action])}</>
                                )
                            }
                        </span>
                    </Col>
                </Row>

                {itemMetaData?.map((actionMeta) => {
                    const metaValue = typeof actionMeta.valueRenderer === "function" ? (
                        actionMeta.valueRenderer({
                            value: logData?.metadata[actionMeta.key],
                            otherFields: logData?.metadata,
                            t,
                            history,
                            users,
                        })
                    ) : logData?.metadata[actionMeta.key];

                    if (actionMeta.hideIfValueIsUndefined && typeof metaValue === "undefined") {
                        return null;
                    }

                    const metaTitle = typeof actionMeta.titleRenderer === "function" ? (
                        actionMeta.titleRenderer({
                            value: logData?.metadata[actionMeta.key],
                            otherFields: logData?.metadata,
                            t,
                        })
                    ) : t(actionMeta.title);

                    return (
                        <Row className="mb-3">
                            <Col sm="6" md="4">
                                <span className="detail-label">
                                    {metaTitle}
                                </span>
                            </Col>
                            <Col sm="6" md="8">
                                <div className="detail-value">
                                    {metaValue}
                                </div>
                            </Col>
                        </Row>
                    );
                })}
            </div>
        );
    };

    return (
        <Modal className="history-log-details-modal" isOpen={showModal} toggle={toggle} size="lg">
            <ModalHeader>
                {t("History Log")} {logData?.secondary_id}
            </ModalHeader>

            <ModalBody>
                {
                    (logDataIsFetching || logDataIsLoading) ? (
                        <div className="d-flex justify-content-center align-items-center">
                            <Spinner animation="border" variant="primary" size="md" />
                        </div>
                    ) : (
                        renderDetailsBody()
                    )
                }
            </ModalBody>

            <ModalFooter>
                <Button onClick={toggle} size="sm" color="primary">
                    {t("Close")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default withNamespaces()(DetailsModal);
